import React, { useMemo } from 'react';
import { FilterItemProps } from './FilterItemDisplay';
import { Chip, Tooltip, Typography, styled } from '@mui/material';
import { isEmptyFilter } from 'helpers/filterHelper';
import { capitalize } from 'helpers/helpers';
import { translateBlock, translateBlockValueWithLabel } from 'i18n';
import { camelize } from 'helpers/syntaxHelper';
import Flex from 'ui/Flex/Flex';
import {
    deserializeListValue,
    deserializeTreeValue,
} from 'components/inputs/TreeTablePicker/helpers';
import { improveLabelForDefaultScopes } from 'components/SentenceFilter/FilterManagees/helpers';
import { TFunction, useTranslation } from 'react-i18next';
import { useGetFilterValues } from 'components/SentenceFilter/FilterManagees/hooks';
import { SelectionTreeT } from 'business/definitions';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';

type FilterItemChipProps = {
    filterValue: SelectionTreeT;
    t: TFunction<'translation', undefined>;
};

const StyChip = styled(Chip, {
    name: 'StyChip',
})(({ theme }) => ({
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.background,
    marginBottom: '2px',
}));

const TreeFilterItemChip = ({ filterValue, t }: FilterItemChipProps) => {
    if (!filterValue) return <></>;

    if (filterValue.isBadValue)
        return (
            <Tooltip
                title={t('common.invalidFilterValue')}
                placement="bottom-start"
                enterDelay={50}
            >
                <HelpRoundedIcon
                    sx={{ color: '#1e2eb8', mr: '0.5rem' }}
                    fontSize="small"
                />
            </Tooltip>
        );

    return (
        <Tooltip
            title={filterValue.label}
            placement="bottom-start"
            enterDelay={50}
        >
            <StyChip size="small" label={filterValue.label} />
        </Tooltip>
    );
};

const TreeFilterItem = ({ filter }: FilterItemProps) => {
    const filterTitle = capitalize(translateBlock(camelize(filter.field)));
    const { t } = useTranslation();

    const StyFilterNoValue = styled(Typography, {
        name: 'StyFilterNoValue',
    })(({ theme }) => ({
        color: theme.palette.mediumGrey,
    }));

    const { data: filterWithValues, isLoading } = useGetFilterValues(filter);

    const filterValues: SelectionTreeT[] = useMemo<SelectionTreeT[]>(() => {
        if (!isLoading && filterWithValues) {
            const scopeOptionsWithUserFriendlyLabels =
                improveLabelForDefaultScopes(filter.scopes ?? [], filter, t);

            const deserializedValues =
                filterWithValues.treeValues !== null &&
                filterWithValues.treeValues !== undefined
                    ? deserializeTreeValue(
                          filter.value,
                          filterWithValues.treeValues,
                          (node) => node.value,
                          scopeOptionsWithUserFriendlyLabels,
                      )
                    : deserializeListValue(
                          filter.value,
                          filterWithValues.values,
                          scopeOptionsWithUserFriendlyLabels,
                          false,
                      );
            return deserializedValues.map(translateBlockValueWithLabel);
        }
        return [];
    }, [filter.value, isLoading, filterWithValues]);

    if (isEmptyFilter(filter))
        return <StyFilterNoValue>{filterTitle}</StyFilterNoValue>;

    if (!filterValues) return <></>;

    return filterValues.length > 1 ? (
        <Flex gap="0 4px">
            <TreeFilterItemChip filterValue={filterValues[0]} t={t} />
            <Tooltip
                title={filterValues
                    .slice(1)
                    .map((f) => f.label)
                    .join(', ')}
                placement="bottom-start"
                enterDelay={50}
            >
                <StyChip size="small" label={`+${filterValues.length - 1}`} />
            </Tooltip>
        </Flex>
    ) : (
        <TreeFilterItemChip filterValue={filterValues[0]} t={t} />
    );
};

export default TreeFilterItem;
